import { Query } from '../../../shared/types/query.types';
import flow from 'lodash/fp/flow';
import isEmpty from 'lodash/fp/isEmpty';
import isEqual from 'lodash/fp/isEqual';
import isUndefined from 'lodash/fp/isUndefined';
import omit from 'lodash/fp/omit';
import omitBy from 'lodash/fp/omitBy';
import pick from 'lodash/fp/pick';
import { useTableDataState } from './table-data.state';

export const NON_FILTER_FIELDS = ['orderBy', 'desc', 'pageSize', 'offset', 'progress', 'inactivatedRowsHidden'];

export const FILTERABLE_FIELDS = {
   itemNos: 'Item number',
   itemName: 'Item name',
   search: 'Item name',
   hfbs: 'HFB',
   pas: 'PA',
   buCodePru: 'PRU',
   buCodePdu: 'PDU',
   familyName: 'Family name',
   itemState: 'Item state',
   part: 'Part',
   partDetail: 'Part detail',
   materialGradeId: 'MAM - Material grade ID',
   search2: 'Material Grade',
   comment: 'Comment',
   updatedBy: 'Updated by',
   progressState: 'Work progress',
   piaImportStatus: 'PIA import status',
   ssd: 'Sale start date',
   eds: 'Sale end date',
   locked: 'Locked',
   unlocked: 'Unlocked',
   dwpDeviates: 'DWP Deviates',
   isBase: 'Is base',
   excludeConnected: 'Exclude connected',
   missingGradeId: 'Missing grade ID',
   outgoingGradeIds: 'Outgoing grade IDs',
};

export function isEmptyFilterValue(value: any) {
   if (value === undefined || value === null || Number.isNaN(value)) {
      return true;
   }

   if (Array.isArray(value)) {
      return value.length === 0;
   }

   if (typeof value === 'object' && Object.keys(value).length === 0) {
      return true;
   }

   if (typeof value === 'string' && value.trim().length === 0) {
      return true;
   }

   if (typeof value === 'boolean' && !value) {
      return true;
   }

   return false;
}

export const useFiltering = () => {
   const [query, setQuery] = useTableDataState((state) => [state.query, state.setQuery]);

   const hasFilterChanged = (newFilter: any) => !isEqual(omit(NON_FILTER_FIELDS)(query), newFilter);

   const isFiltered = !flow(omit(NON_FILTER_FIELDS), isEmpty)(query);

   const reset = () => {
      const preservedFields = flow(pick(NON_FILTER_FIELDS), omitBy(isUndefined))(query) as Query;
      setQuery(preservedFields);
   };

   const removeFilter = (field: any, value?: any) => {
      let fieldValue: any = Object(query)[field];
      if (Array.isArray(fieldValue) && value !== undefined) {
         let newFilter: string[] = fieldValue.filter((item) => item !== value);
         let queryClone = structuredClone(query);
         if (newFilter.length > 0) {
            Object(queryClone)[field] = newFilter;
         } else {
            delete Object(queryClone)[field];
         }
         queryClone.offset = 0;
         setQuery(queryClone);
      } else {
         const newQuery = omit([field])(query) as Query;
         setQuery({ ...newQuery, offset: 0 });
      }
   };

   return { reset, isFiltered, hasFilterChanged, removeFilter };
};
